import { Model, fields } from '@gasman/smush';
import { Pattern } from './pattern';
import { FORMAT_NAME } from './format';
import { Sample } from './sample';
import { Ornament } from './ornament';
import { UndoBuffer } from '../../common/undo';
import { readSTC, writeSTC } from 'stcformat';

class Song extends Model([
    new fields.IntegerField('tempo', { min: 1, max: 15, default: 5 }),
    new fields.IntegerField('length', {
        label: 'Song len',
        min: 1,
        max: 255,
        default: 1,
    }),
    new fields.LazyModelListField('samples', Sample, {
        elementName: 'sample',
        startIndex: 1,
        endIndex: 16,
    }),
    new fields.LazyModelListField('ornaments', Ornament, {
        elementName: 'ornament',
        startIndex: 1,
        endIndex: 16,
        defaultData: [],
    }),
    new fields.LazyListField(
        'positions',
        new fields.StructField('position', [
            new fields.IntegerField('pattern', {
                min: 1,
                max: 31,
                default: 1,
                label: 'Pattern',
            }),
            new fields.IntegerField('transpose', {
                min: -127,
                max: 127,
                default: 0,
                label: 'Transpose',
            }),
        ]),
        { startIndex: 1 },
    ),
    new fields.LazyModelListField('patterns', Pattern, {
        elementName: 'pattern',
        startIndex: 1,
    }),
]) {
    constructor(songData) {
        super(songData);
        this.undoBuffer = new UndoBuffer();
    }

    getChannelCount() {
        let Pattern = this.constructor.fields.patterns.model;
        return Pattern.fields.channels.fieldList.length;
    }

    serialize() {
        let result = super.serialize();
        result['engine'] = FORMAT_NAME;
        return result;
    }

    asBlob() {
        return new Blob([JSON.stringify(this.serialize())], {
            type: 'application/json',
        });
    }

    undo() {
        this.undoBuffer.undo(this);
    }
    redo() {
        this.undoBuffer.redo(this);
    }

    static fromSTC(buf) {
        let stc = readSTC(buf);
        /* stcformat returns samples/ornaments/patterns as 0-based arrays with a dummy
        0th element, but the Song constructor wants just the meaningful data starting
        at index 1. These arrays are also sparse (unused slots are holes) so we fill
        them with {}. */
        let toDenseArray = (sparseArray) =>
            Array.from(sparseArray.slice(1), (item) => item || {});
        return new Song({
            ...stc,
            samples: toDenseArray(stc.samples),
            ornaments: toDenseArray(stc.ornaments),
            patterns: toDenseArray(stc.patterns),
        });
    }

    exportFormats = ['STC'];
    asSTC() {
        const songData = this.serialize();
        songData.samples = [null, ...songData.samples];
        songData.ornaments = [
            {
                tones: [
                    0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
                    0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
                ],
            },
            ...songData.ornaments,
        ];
        songData.patterns = [null, ...songData.patterns];
        return writeSTC(songData);
    }
    exportToFormat(format) {
        if (format === 'STC') {
            return {
                data: this.asSTC(),
                filename: 'song.stc',
            };
        } else {
            throw new Error(`Unsupported export format: ${format}`);
        }
    }
    convertTargetEngine = 'TurboSoundtracker';
    convertToOtherEngine() {
        const songData = this.serialize();
        for (let pattern of songData.patterns) {
            for (let i = 0; i < 3; i++) {
                const newChannel = [];
                for (let j = 0; j < pattern.length; j++) {
                    newChannel[j] = ['---', 0, 0, 0];
                }
                pattern.channels.push(newChannel);
            }
        }
        const { TurbosoundtrackerEngine } = require('../turbosoundtracker');
        const convertedSong = new TurbosoundtrackerEngine.Song(songData);
        return {
            song: convertedSong,
            engine: TurbosoundtrackerEngine,
        };
    }
}

export { Song };
